// Pace-of-play timer + status engine.
//
// A "group" is the set of teams sharing a tee box — same flightId + same
// startingHole. The shotgun layout produces 18 groups (one per hole),
// each typically holding 2 teams in match play. Every group gets its
// own independent 15-minute hole timer in Firebase at
//   tournaments/{id}/paceOfPlay/{groupId}/{
//     holeStartTime, currentHole, startingHole, lastUpdatedAt, scorecardTeamIds
//   }
//
// Timer rule: when any team in a group submits a hole score, we reset
// holeStartTime to now and advance currentHole to the next hole. The
// scorecard + Command Center then read live time-remaining vs. the
// 15-minute budget and decide green / yellow / red / delayed entirely
// from the snapshot — status is NOT stored, so a stale tab and a fresh
// one always agree.
//
// Source vs delayed:
//   - A group is RED when more than 15 minutes have elapsed since its
//     holeStartTime.
//   - When red, we look at every OTHER group that's also red AND has
//     completed at least as many holes on the course as this group AND
//     went red earlier (holeStartTime older). If any exist, this group
//     is "delayed" (collateral). If none exist, this group is the
//     source.
//   - "Completed holes on the course" for shotgun is
//     ((currentHole - startingHole + 18) % 18) so wrap-around (hole 18
//     → hole 1) doesn't confuse the comparison.

import { ref, set, update } from 'firebase/database'

import { db } from './firebase.js'

export const PACE_LIMIT_MS = 15 * 60 * 1000     // red threshold
export const PACE_WARN_MS = 12 * 60 * 1000      // yellow threshold (3 min remaining)

// Stable derivation. A pair of teams that share startingHole 7 in
// flight "championship" lands in groupId "championship__h07". The
// zero-pad keeps Firebase ordering correct when admins glance at raw
// data.
export function getGroupId(flightId, startingHole) {
  const fid = String(flightId || 'unknown')
  const sh = Number(startingHole) || 1
  return `${fid}__h${String(sh).padStart(2, '0')}`
}

// Walk one hole forward, wrapping 18 → 1 so a shotgun starting on hole
// 17 advances 17 → 18 → 1 → 2 → … through 18 holes total.
export function nextHole(h) {
  const n = Number(h) || 1
  return n >= 18 ? 1 : n + 1
}

// "How many holes has this group completed?" — relative to their
// startingHole. A shotgun group on hole 12 that started at hole 9 has
// completed 3 holes (9, 10, 11). Used for "is there a slow group ahead
// of me?" comparison.
function completedHoles(group) {
  const sh = Number(group?.startingHole) || 1
  const cur = Number(group?.currentHole) || sh
  return ((cur - sh) + 18) % 18
}

// Called from the scorecard's TeamHoleEntry onSaved. Resets the
// group's timer to now and advances currentHole. First save on a hole
// (the one that started the round) lands the very first timer record.
// `teamId` is recorded under scorecardTeamIds so the admin can see who
// in the group submitted last (and so any team in the group hits the
// same record on their next save).
export async function recordHoleSavedForGroup({
  tournamentId, flightId, startingHole, completedHole, teamId,
}) {
  if (!tournamentId || !flightId || !startingHole) return
  const groupId = getGroupId(flightId, startingHole)
  const path = `tournaments/${tournamentId}/paceOfPlay/${groupId}`
  // Patch — `update` not `set` — so we don't blow away scorecardTeamIds
  // contributed by the partner team a few seconds earlier.
  const now = Date.now()
  const newCurrentHole = nextHole(completedHole)
  await update(ref(db, path), {
    holeStartTime: now,
    currentHole: newCurrentHole,
    startingHole: Number(startingHole),
    flightId: String(flightId),
    lastUpdatedAt: now,
    lastUpdatedBy: String(teamId || ''),
  })
  // Also tag this team into the scorecardTeamIds child so the Command
  // Center can render "Team A and Team B" for the group at a glance.
  if (teamId) {
    await set(
      ref(db, `${path}/scorecardTeamIds/${teamId}`),
      true,
    )
  }
}

// Pure status compute. Returns:
//   {
//     status:    'green' | 'yellow' | 'red' | 'delayed',
//     elapsedMs, remainingMs,
//     minutesOver,
//     isSource,
//   }
//
// `groups` is the full /paceOfPlay map (a snapshot from RTDB).
// `now` is the current epoch ms (passed in so tests can pin time and
// so the same call is reused for the live setInterval re-render).
// `limits` optionally overrides the 15/12-min thresholds — the Test
// Station's Pace of Play tile passes a 5/3-min budget here so testers
// can verify yellow/red without waiting the full quarter-hour. Real
// tournaments leave it unset and inherit the production constants.
export function computeGroupStatus(group, groups, now = Date.now(), limits = null) {
  const paceLimitMs = Number(limits?.paceLimitMs) > 0 ? Number(limits.paceLimitMs) : PACE_LIMIT_MS
  const paceWarnMs  = Number(limits?.paceWarnMs)  > 0 ? Number(limits.paceWarnMs)  : PACE_WARN_MS
  if (!group || !group.holeStartTime) {
    return { status: 'green', elapsedMs: 0, remainingMs: paceLimitMs, minutesOver: 0, isSource: false }
  }
  const elapsedMs = Math.max(0, now - Number(group.holeStartTime))
  const remainingMs = paceLimitMs - elapsedMs

  if (elapsedMs < paceWarnMs) {
    return { status: 'green', elapsedMs, remainingMs, minutesOver: 0, isSource: false }
  }
  if (elapsedMs < paceLimitMs) {
    return { status: 'yellow', elapsedMs, remainingMs, minutesOver: 0, isSource: false }
  }

  // Red threshold passed. Decide source vs delayed.
  const myCompleted = completedHoles(group)
  let blockedBy = null
  for (const other of Object.values(groups || {})) {
    if (!other || other === group) continue
    if (!other.holeStartTime) continue
    const otherElapsed = now - Number(other.holeStartTime)
    if (otherElapsed < paceLimitMs) continue
    // Is this OTHER group ahead of (or even with) me on the course AND
    // went red earlier than me? If so, they're the upstream blocker.
    if (completedHoles(other) >= myCompleted && Number(other.holeStartTime) < Number(group.holeStartTime)) {
      blockedBy = other
      break
    }
  }
  const minutesOver = Math.floor((elapsedMs - paceLimitMs) / 60000)
  if (blockedBy) {
    return { status: 'delayed', elapsedMs, remainingMs, minutesOver, isSource: false }
  }
  return { status: 'red', elapsedMs, remainingMs, minutesOver, isSource: true }
}

// "5:42" style mm:ss for the live countdown. Negative remainingMs (red
// state) is rendered as the magnitude — the banner that consumes it
// already says "over time" so the sign is implicit.
export function formatRemaining(ms) {
  const abs = Math.max(0, Math.floor(Math.abs(ms) / 1000))
  const m = Math.floor(abs / 60)
  const s = abs % 60
  return `${m}:${String(s).padStart(2, '0')}`
}
